import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Package, Pencil, Trash2, X, Save, Search, Filter, Plus, ChevronLeft, ChevronRight, Image, FileText, Upload, Eye, Settings, CheckSquare, Square, MinusSquare } from 'lucide-react';
import CsvImportModal from '../components/CsvImportModal';
import CreateButton from '../components/CreateButton';

const ITEMS_PER_PAGE = 30;

const IVA_TYPES = {
  1: '4%',
  2: '10%',
  3: '21%',
};

const ProductManagement = () => {
  const { token, API_URL } = useAuth();
  const [products, setProducts] = useState([]);
  const [totalProducts, setTotalProducts] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingProduct, setDeletingProduct] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [page, setPage] = useState(1);
  const [families, setFamilies] = useState([]);
  const [familyFilter, setFamilyFilter] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState('');
  const [saving, setSaving] = useState(false);

  // File management state
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [filesProduct, setFilesProduct] = useState(null);
  const [productFiles, setProductFiles] = useState({ imagenes: [], fichas: [] });
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [uploadingFichas, setUploadingFichas] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  // Families management state
  const [showFamiliesModal, setShowFamiliesModal] = useState(false);
  const [familiesWithCount, setFamiliesWithCount] = useState([]);
  const [familiesLoading, setFamiliesLoading] = useState(false);
  const [editingFamily, setEditingFamily] = useState(null);
  const [editFamilyName, setEditFamilyName] = useState('');
  const [newFamilyName, setNewFamilyName] = useState('');
  const [familySaving, setFamilySaving] = useState(false);
  const [familyError, setFamilyError] = useState('');

  // Bulk update state
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);

  // Excel bulk update state
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [excelUploading, setExcelUploading] = useState(false);
  const [excelResult, setExcelResult] = useState(null);

  // Bulk file upload state
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkFiles, setBulkFiles] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const [conflictSelections, setConflictSelections] = useState({});
  const [conflictUploading, setConflictUploading] = useState({});

  // Los archivos del catalogo los sirve el BACKEND por streaming desde el FTP
  // (ruta /api/catalogo/{imagenes|fichas}/:filename). Antes se apuntaba a
  // comprassanitarias.com/catalogo/... pero la SPA no sirve /catalogo/ -> caia al
  // index.html: el PDF "abria" la home y las imagenes salian en blanco.
  const CATALOGO_URL = `${API_URL}/catalogo`;

  const emptyForm = {
    nombre: '', raiz: '', familia: '', precio_coste: '', cod_barras: '',
    tipo_iva: 3, cantidad_embalaje: 1,
  };
  const [form, setForm] = useState(emptyForm);
  const [newProductImages, setNewProductImages] = useState(null);
  const [newProductFichas, setNewProductFichas] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);

  const handleImportProducts = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${API_URL}/products/import`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` },
      body: formData
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Error al importar');
    // Refresh products list after import
    fetchProducts(page, searchTerm, familyFilter, visibilityFilter);
    return data;
  };

  // Fetch families for dropdown
  useEffect(() => {
    const fetchFamilies = async () => {
      try {
        const res = await fetch(`${API_URL}/families`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Error al cargar familias');
        const data = await res.json();
        setFamilies(data.families || data || []);
      } catch (err) {
        console.error('Error fetching families:', err);
      }
    };
    fetchFamilies();
  }, [API_URL, token]);

  const fetchProducts = useCallback(async (currentPage = 1, search = '', familia = '', visible = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, limit: ITEMS_PER_PAGE });
      if (search.trim()) params.append('search', search.trim());
      if (familia) params.append('familia', familia);
      if (visible !== '') params.append('visible', visible);

      const res = await fetch(`${API_URL}/products?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar productos');
      const data = await res.json();
      setProducts(data.products || []);
      setTotalProducts(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchProducts(page, searchTerm, familyFilter, visibilityFilter);
  }, [page, familyFilter, visibilityFilter]);

  // Debounced search
  useEffect(() => {
    if (searchTimeout) clearTimeout(searchTimeout);
    const timeout = setTimeout(() => {
      setPage(1);
      fetchProducts(1, searchTerm, familyFilter, visibilityFilter);
    }, 400);
    setSearchTimeout(timeout);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  const openCreate = () => {
    setEditingProduct(null);
    setForm(emptyForm);
    setNewProductImages(null);
    setNewProductFichas(null);
    setShowModal(true);
  };

  const openEdit = (product) => {
    setEditingProduct(product);
    setForm({
      nombre: product.nombre || '',
      raiz: product.raiz || '',
      familia: product.familia || product.id_familia || '',
      precio_coste: product.precio_coste || '',
      cod_barras: product.cod_barras || '',
      tipo_iva: product.tipo_iva || 3,
      cantidad_embalaje: product.cantidad_embalaje || 1,
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const url = editingProduct ? `${API_URL}/products/${editingProduct.id}` : `${API_URL}/products`;
      const method = editingProduct ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al guardar');

      // For new products: upload image and ficha if provided
      const productId = editingProduct ? editingProduct.id : data.product?.id;
      const uploadWarnings = [];
      if (!editingProduct && productId) {
        if (newProductImages && newProductImages.length > 0) {
          try {
            const fd = new FormData();
            for (let i = 0; i < newProductImages.length; i++) fd.append('imagenes', newProductImages[i]);
            const imgRes = await fetch(`${API_URL}/catalogo/producto/${productId}/imagen`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
              body: fd,
            });
            if (!imgRes.ok) {
              const errData = await imgRes.json().catch(() => ({}));
              uploadWarnings.push(`Imágenes: ${errData.message || imgRes.status}`);
            }
          } catch (e) {
            uploadWarnings.push(`Imágenes: ${e.message}`);
          }
        }
        if (newProductFichas && newProductFichas.length > 0) {
          try {
            const fd = new FormData();
            for (let i = 0; i < newProductFichas.length; i++) fd.append('fichas', newProductFichas[i]);
            const fichaRes = await fetch(`${API_URL}/catalogo/producto/${productId}/ficha`, {
              method: 'POST',
              headers: { 'Authorization': `Bearer ${token}` },
              body: fd,
            });
            if (!fichaRes.ok) {
              const errData = await fichaRes.json().catch(() => ({}));
              uploadWarnings.push(`Fichas: ${errData.message || fichaRes.status}`);
            }
          } catch (e) {
            uploadWarnings.push(`Fichas: ${e.message}`);
          }
        }
      }
      if (uploadWarnings.length > 0) {
        setError(`Producto creado pero hubo problemas con archivos: ${uploadWarnings.join(' | ')}`);
      }

      setShowModal(false);
      setNewProductImages(null);
      setNewProductFichas(null);
      fetchProducts(page, searchTerm, familyFilter, visibilityFilter);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (product) => {
    setDeletingProduct(product);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!deletingProduct) return;
    try {
      const res = await fetch(`${API_URL}/products/${deletingProduct.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al eliminar');
      setShowDeleteModal(false);
      setDeletingProduct(null);
      fetchProducts(page, searchTerm, familyFilter, visibilityFilter);
    } catch (err) {
      setError(err.message);
    }
  };

  const totalPages = Math.ceil(totalProducts / ITEMS_PER_PAGE);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const getFamilyName = (familyId) => {
    const fam = families.find(f => f.id === familyId || f.id === Number(familyId));
    return fam ? fam.nombre : familyId || '—';
  };

  // ── Families management functions ──────────────────────────────
  const openFamiliesModal = async () => {
    setShowFamiliesModal(true);
    setFamilyError('');
    setNewFamilyName('');
    setEditingFamily(null);
    await fetchFamiliesWithCount();
  };

  const fetchFamiliesWithCount = async () => {
    setFamiliesLoading(true);
    try {
      const res = await fetch(`${API_URL}/families-with-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar familias');
      const data = await res.json();
      setFamiliesWithCount(data);
    } catch (err) {
      setFamilyError(err.message);
    } finally {
      setFamiliesLoading(false);
    }
  };

  const handleCreateFamily = async () => {
    if (!newFamilyName.trim()) return;
    setFamilySaving(true);
    setFamilyError('');
    try {
      const res = await fetch(`${API_URL}/families`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre: newFamilyName.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al crear familia');
      setNewFamilyName('');
      await fetchFamiliesWithCount();
      // Refresh families dropdown
      const res2 = await fetch(`${API_URL}/families`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data2 = await res2.json();
      setFamilies(data2.families || data2 || []);
    } catch (err) {
      setFamilyError(err.message);
    } finally {
      setFamilySaving(false);
    }
  };

  const handleUpdateFamily = async (id) => {
    if (!editFamilyName.trim()) return;
    setFamilySaving(true);
    setFamilyError('');
    try {
      const res = await fetch(`${API_URL}/families/${id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre: editFamilyName.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al actualizar familia');
      setEditingFamily(null);
      setEditFamilyName('');
      await fetchFamiliesWithCount();
      const res2 = await fetch(`${API_URL}/families`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data2 = await res2.json();
      setFamilies(data2.families || data2 || []);
    } catch (err) {
      setFamilyError(err.message);
    } finally {
      setFamilySaving(false);
    }
  };

  const handleDeleteFamily = async (id) => {
    setFamilySaving(true);
    setFamilyError('');
    try {
      const res = await fetch(`${API_URL}/families/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error al eliminar familia');
      await fetchFamiliesWithCount();
      const res2 = await fetch(`${API_URL}/families`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data2 = await res2.json();
      setFamilies(data2.families || data2 || []);
    } catch (err) {
      setFamilyError(err.message);
    } finally {
      setFamilySaving(false);
    }
  };

  // ── Bulk update functions ──────────────────────────────────────
  const toggleProductSelection = (productId) => {
    setSelectedProducts(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedProducts.size === products.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(products.map(p => p.id)));
    }
  };

  const openBulkModal = () => {
    setBulkAction('');
    setBulkValue('');
    setShowBulkModal(true);
  };

  const handleBulkUpdate = async () => {
    if (!bulkAction || selectedProducts.size === 0) return;
    setBulkSaving(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/products/bulk-update`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productIds: Array.from(selectedProducts),
          action: bulkAction,
          value: bulkValue
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error en actualización masiva');
      setShowBulkModal(false);
      setSelectedProducts(new Set());
      fetchProducts(page, searchTerm, familyFilter, visibilityFilter);
    } catch (err) {
      setError(err.message);
    } finally {
      setBulkSaving(false);
    }
  };

  // ── Excel bulk update functions ────────────────────────────────
  // UN solo Excel con TODAS las columnas posibles del producto.
  // Cualquier columna vacía se ignora — solo se actualiza lo que el usuario
  // rellene. La única columna obligatoria es A (raiz).
  const openExcelModal = () => {
    setExcelFile(null);
    setExcelResult(null);
    setExcelUploading(false);
    setShowExcelModal(true);
  };

  const handleExcelUpload = async () => {
    if (!excelFile || excelUploading) return;
    setExcelUploading(true);
    setExcelResult(null);
    try {
      const formData = new FormData();
      formData.append('file', excelFile);
      const res = await fetch(`${API_URL}/products/bulk-excel-update-unified`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error en la actualización');
      setExcelResult(data);
      fetchProducts(page, searchTerm, familyFilter, visibilityFilter);
    } catch (err) {
      setExcelResult({ error: true, message: err.message });
    } finally {
      setExcelUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const res = await fetch(`${API_URL}/products/excel-template-unified`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('No se pudo descargar la plantilla');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla_productos_unificada.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('Error al descargar plantilla: ' + err.message);
    }
  };

  const EXCEL_FORMAT_INFO = {
    title: 'Actualización masiva de productos desde Excel',
    columns: 'A: raiz (REQ) | B: nombre (info) | C: proveedor | D: precio_coste | E: cantidad_embalaje | F: familia (id) | G: tipo_iva (id) | H: cod_barras | I: visible (0/1)',
    description: 'Excel con TODAS las columnas posibles del producto. Cualquier celda vacía se ignora — solo se actualiza lo que rellene. Solo la columna "raiz" es obligatoria. Descargue la plantilla de ejemplo con el botón de abajo.'
  };

  // ── Bulk file upload functions ────────────────────────────────
  const openBulkUploadModal = () => {
    setBulkFiles(null);
    setBulkUploadResult(null);
    setBulkUploading(false);
    setConflictSelections({});
    setConflictUploading({});
    setShowBulkUploadModal(true);
  };

  const handleBulkFileUpload = async () => {
    if (!bulkFiles || bulkFiles.length === 0 || bulkUploading) return;
    setBulkUploading(true);
    setBulkUploadResult(null);
    try {
      const formData = new FormData();
      for (const file of bulkFiles) {
        formData.append('files', file);
      }
      const res = await fetch(`${API_URL}/catalogo/bulk-upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Error en la carga masiva');
      setBulkUploadResult(data);
    } catch (err) {
      setBulkUploadResult({ error: true, message: err.message });
    } finally {
      setBulkUploading(false);
    }
  };

  const handleConflictResolve = async (conflictIndex) => {
    const conflict = bulkUploadResult.conflicts[conflictIndex];
    const selectedProductId = conflictSelections[conflictIndex];
    if (!selectedProductId) return;

    setConflictUploading(prev => ({ ...prev, [conflictIndex]: true }));
    try {
      // Find the original file from bulkFiles by matching the filename
      const originalFile = Array.from(bulkFiles).find(f => f.name === conflict.filename);
      if (!originalFile) throw new Error('Archivo original no encontrado');

      const formData = new FormData();
      const isPdf = conflict.isPdf;
      formData.append(isPdf ? 'fichas' : 'imagenes', originalFile);

      const endpoint = isPdf
        ? `${API_URL}/catalogo/producto/${selectedProductId}/ficha`
        : `${API_URL}/catalogo/producto/${selectedProductId}/imagen`;

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.message || 'Error al subir archivo');
      }

      // Mark this conflict as resolved
      setBulkUploadResult(prev => ({
        ...prev,
        conflicts: prev.conflicts.map((c, i) =>
          i === conflictIndex ? { ...c, resolved: true, resolvedProductId: selectedProductId } : c
        )
      }));
    } catch (err) {
      alert(`Error: ${err.message}`);
    } finally {
      setConflictUploading(prev => ({ ...prev, [conflictIndex]: false }));
    }
  };

  // Clear selection when page/filters change
  useEffect(() => {
    setSelectedProducts(new Set());
  }, [page, familyFilter, visibilityFilter, searchTerm]);

  // ── File management functions ──────────────────────────────────
  const openFilesModal = async (product) => {
    setFilesProduct(product);
    setShowFilesModal(true);
    setProductFiles({ imagenes: [], fichas: [] });
    setFilesLoading(true);
    try {
      const res = await fetch(`${API_URL}/catalogo/producto/${product.id}/archivos`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al cargar archivos');
      const data = await res.json();
      setProductFiles({ imagenes: data.imagenes || [], fichas: data.fichas || [] });
    } catch (err) {
      console.error('Error fetching files:', err);
    } finally {
      setFilesLoading(false);
    }
  };

  const handleUploadImages = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !filesProduct) return;
    setUploadingImages(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) formData.append('imagenes', files[i]);
      const res = await fetch(`${API_URL}/catalogo/producto/${filesProduct.id}/imagen`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) throw new Error('Error al subir imágenes');
      // Refresh files list and product list
      await openFilesModal(filesProduct);
      fetchProducts(page, searchTerm, familyFilter, visibilityFilter);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadingImages(false);
      e.target.value = '';
    }
  };

  const handleUploadFichas = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0 || !filesProduct) return;
    setUploadingFichas(true);
    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) formData.append('fichas', files[i]);
      const res = await fetch(`${API_URL}/catalogo/producto/${filesProduct.id}/ficha`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      if (!res.ok) throw new Error('Error al subir fichas');
      await openFilesModal(filesProduct);
      fetchProducts(page, searchTerm, familyFilter, visibilityFilter);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadingFichas(false);
      e.target.value = '';
    }
  };

  const handleDeleteImage = async (filename) => {
    try {
      const res = await fetch(`${API_URL}/catalogo/imagen/${filename}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al eliminar imagen');
      await openFilesModal(filesProduct);
      fetchProducts(page, searchTerm, familyFilter, visibilityFilter);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteFicha = async (filename) => {
    try {
      const res = await fetch(`${API_URL}/catalogo/ficha/${filename}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error al eliminar ficha');
      await openFilesModal(filesProduct);
      fetchProducts(page, searchTerm, familyFilter, visibilityFilter);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="h-full flex flex-col">

      {/* Header bar */}
      <div className="bg-white shadow-sm border-b border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="p-3 rounded-full" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
              <Package className="w-6 h-6" style={{ color: '#0099FF' }} />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Productos</h1>
              <p className="text-sm text-gray-500">Gestión de productos y catálogo</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openBulkUploadModal}
              className="px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <Image size={16} /> Carga Masiva Archivos
            </button>
            <button
              onClick={openExcelModal}
              className="px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <Upload size={16} /> Actualización Excel
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="px-4 py-2 rounded-md text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
            >
              <Upload size={16} /> Importar CSV
            </button>
            <CreateButton onClick={openCreate}>Nuevo Producto</CreateButton>
          </div>
        </div>
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mt-4 flex items-center justify-between">
            <span className="text-sm">{error}</span>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X size={16} /></button>
          </div>
        )}
      </div>

      {/* Filter bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre o código..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Family dropdown */}
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-400" />
            <select
              value={familyFilter}
              onChange={(e) => { setFamilyFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todas las familias</option>
              {families.map(f => (
                <option key={f.id} value={f.id}>{f.nombre}</option>
              ))}
            </select>
            <button
              onClick={openFamiliesModal}
              className="px-3 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-1.5"
              title="Gestionar Familias"
            >
              <Settings size={14} /> Familias
            </button>
          </div>

          {/* Visibility filter */}
          <div className="flex gap-1">
            {[
              { value: '', label: 'Todos' },
              { value: '1', label: 'Visibles' },
              { value: '0', label: 'Ocultos' },
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => { setVisibilityFilter(opt.value); setPage(1); }}
                className="px-3 py-2 rounded-md text-xs font-medium transition-colors border"
                style={visibilityFilter === opt.value
                  ? { backgroundColor: 'rgba(0,153,255,0.08)', color: '#0077CC', borderColor: '#0099FF' }
                  : { color: '#6b7280', borderColor: '#e5e7eb' }
                }
              >
                {opt.label}
              </button>
            ))}
          </div>

          {selectedProducts.size > 0 && (
            <button
              onClick={openBulkModal}
              style={{ backgroundColor: '#0099FF' }}
              className="ml-auto px-3 py-2 rounded-lg text-sm font-medium text-white hover:opacity-90 transition-opacity flex items-center gap-1.5"
            >
              <CheckSquare size={14} /> Actualizar Seleccionados ({selectedProducts.size})
            </button>
          )}
          <span className={`text-xs text-gray-400 ${selectedProducts.size === 0 ? 'ml-auto' : ''}`}>{totalProducts} productos</span>
        </div>
      </div>

      {/* Table area (scrollable) */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-center w-10">
                  <button onClick={toggleSelectAll} className="text-gray-400 hover:text-gray-600 transition-colors">
                    {products.length > 0 && selectedProducts.size === products.length
                      ? <CheckSquare size={16} style={{ color: '#0099FF' }} />
                      : selectedProducts.size > 0
                        ? <MinusSquare size={16} style={{ color: '#0099FF' }} />
                        : <Square size={16} />
                    }
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ref</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Familia</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Precio Coste</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">IVA</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Embalaje</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Archivos</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Acciones</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && products.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto" style={{ borderColor: '#0099FF' }}></div>
                  </td>
                </tr>
              ) : products.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-12 text-center">
                    <Package size={36} className="mx-auto mb-3 text-gray-300" />
                    <p className="text-sm text-gray-500">No se encontraron productos</p>
                    <p className="text-xs text-gray-400 mt-1">Intente con otros criterios de búsqueda</p>
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id} className={`hover:bg-gray-50 transition-colors ${selectedProducts.has(product.id) ? 'bg-blue-50' : ''}`}>
                    <td className="px-3 py-4 text-center w-10">
                      <button onClick={() => toggleProductSelection(product.id)} className="text-gray-400 hover:text-gray-600 transition-colors">
                        {selectedProducts.has(product.id)
                          ? <CheckSquare size={16} style={{ color: '#0099FF' }} />
                          : <Square size={16} />
                        }
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500">
                      {product.raiz || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {product.nombre}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {product.familia_nombre || getFamilyName(product.familia || product.id_familia)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {product.precio_coste !== undefined && product.precio_coste !== null
                        ? `${Number(product.precio_coste).toFixed(2)} €`
                        : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {IVA_TYPES[product.tipo_iva] || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      {product.cantidad_embalaje || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {Number(product.visible) === 1 ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          Activo
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                          Inactivo
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        onClick={() => openFilesModal(product)}
                        className="inline-flex items-center gap-1.5 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
                        title="Gestionar archivos"
                      >
                        {product.imagen ? (
                          <img
                            src={`${CATALOGO_URL}/imagenes/${product.imagen}`}
                            alt={product.nombre}
                            className="w-7 h-7 object-cover rounded border border-gray-200"
                            onError={(e) => {
                              // Fallback como el PHP class_producto.php: si falla la
                              // extensión guardada, probamos .png → .jpg → .jpeg → .gif
                              const base = String(product.imagen).replace(/\.[^.]+$/, '');
                              const tried = e.target.dataset.tried ? e.target.dataset.tried.split(',') : [];
                              const exts = ['.png', '.jpg', '.jpeg', '.gif'].filter(x => !tried.includes(x));
                              if (exts.length === 0) { e.target.style.display = 'none'; return; }
                              const nextExt = exts[0];
                              e.target.dataset.tried = [...tried, nextExt].join(',');
                              e.target.src = `${CATALOGO_URL}/imagenes/${base}${nextExt}`;
                            }}
                          />
                        ) : (
                          <Image size={16} className="text-gray-300" />
                        )}
                        <FileText size={16} className={product.pdf ? 'text-green-500' : 'text-gray-300'} />
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(product)}
                          className="p-1.5 rounded text-gray-400 hover:text-blue-600 transition-colors"
                          title="Editar"
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          onClick={() => confirmDelete(product)}
                          className="p-1.5 rounded text-gray-400 hover:text-red-500 transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Mostrando {((page - 1) * ITEMS_PER_PAGE) + 1} a {Math.min(page * ITEMS_PER_PAGE, totalProducts)} de {totalProducts} productos
            </p>
            <div className="flex space-x-1">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
              >
                <ChevronLeft size={14} /> Anterior
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (page <= 3) {
                  pageNum = i + 1;
                } else if (page >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = page - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${
                      page === pageNum
                        ? 'text-white'
                        : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                    style={page === pageNum ? { backgroundColor: '#0099FF' } : {}}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 text-sm rounded-md border border-gray-300 text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors flex items-center gap-1"
              >
                Siguiente <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-lg z-10">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-1 rounded hover:bg-gray-100 transition-colors">
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={(e) => updateField('nombre', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Raíz / Código</label>
                  <input
                    type="text"
                    value={form.raiz}
                    onChange={(e) => updateField('raiz', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Familia</label>
                  <select
                    value={form.familia}
                    onChange={(e) => updateField('familia', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Sin familia</option>
                    {families.map(f => (
                      <option key={f.id} value={f.id}>{f.nombre}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Precio Coste</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.precio_coste}
                    onChange={(e) => updateField('precio_coste', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cód. Barras</label>
                  <input
                    type="text"
                    value={form.cod_barras}
                    onChange={(e) => updateField('cod_barras', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tipo IVA</label>
                  <select
                    value={form.tipo_iva}
                    onChange={(e) => updateField('tipo_iva', parseInt(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value={1}>4%</option>
                    <option value={2}>10%</option>
                    <option value={3}>21%</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad Embalaje</label>
                  <input
                    type="number"
                    value={form.cantidad_embalaje}
                    onChange={(e) => updateField('cantidad_embalaje', parseInt(e.target.value) || 1)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* File uploads — only on create */}
              {!editingProduct && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Archivos adjuntos (opcional)</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Imágenes</label>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/gif"
                        multiple
                        onChange={(e) => setNewProductImages(e.target.files)}
                        className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                      {newProductImages && newProductImages.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">{newProductImages.length} imagen(es) seleccionada(s)</p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Fichas técnicas (PDF)</label>
                      <input
                        type="file"
                        accept="application/pdf"
                        multiple
                        onChange={(e) => setNewProductFichas(e.target.files)}
                        className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-green-50 file:text-green-700 hover:file:bg-green-100"
                      />
                      {newProductFichas && newProductFichas.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">{newProductFichas.length} ficha(s) seleccionada(s)</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg sticky bottom-0 z-10">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: '#0099FF' }}
              >
                {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={14} />}
                {editingProduct ? 'Actualizar' : 'Crear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full mx-4">
            <div className="px-6 py-5">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 rounded-full bg-red-50">
                  <Trash2 size={20} className="text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Confirmar eliminación</h3>
              </div>
              <p className="text-sm text-gray-600">
                ¿Está seguro de que desea eliminar el producto <strong>{deletingProduct?.nombre}</strong>?
                Esta acción cambiará la visibilidad del producto.
              </p>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => { setShowDeleteModal(false); setDeletingProduct(null); }}
                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <Trash2 size={14} /> Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Files Management Modal */}
      {showFilesModal && filesProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-lg z-10">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Gestionar Archivos</h3>
                <p className="text-sm text-gray-500">{filesProduct.nombre} — <span className="font-mono">{filesProduct.raiz || 'Sin código'}</span></p>
              </div>
              <button onClick={() => { setShowFilesModal(false); setFilesProduct(null); }} className="p-1 rounded hover:bg-gray-100 transition-colors">
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 space-y-6">
              {filesLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#0099FF' }}></div>
                </div>
              ) : (
                <>
                  {/* Images Section */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <Image size={16} style={{ color: '#0099FF' }} /> Imágenes
                      </h4>
                      <label className="cursor-pointer px-3 py-1.5 rounded-md text-xs font-medium text-white transition-colors flex items-center gap-1.5 hover:opacity-90" style={{ backgroundColor: '#0099FF' }}>
                        {uploadingImages ? (
                          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Upload size={13} />
                        )}
                        Subir Imágenes
                        <input type="file" accept="image/*" multiple onChange={handleUploadImages} className="hidden" disabled={uploadingImages} />
                      </label>
                    </div>
                    {productFiles.imagenes.length === 0 ? (
                      <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                        <Image size={28} className="mx-auto mb-2 text-gray-300" />
                        <p className="text-xs text-gray-400">No hay imágenes cargadas</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-3">
                        {productFiles.imagenes.map((img) => (
                          <div key={img.filename} className="relative group rounded-lg overflow-hidden border border-gray-200 hover:border-blue-300 transition-colors">
                            <img
                              src={img.url || `${CATALOGO_URL}/imagenes/${img.filename}`}
                              alt={img.filename}
                              className="w-full h-[120px] object-cover cursor-pointer"
                              onClick={() => setLightboxUrl(img.url || `${CATALOGO_URL}/imagenes/${img.filename}`)}
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center">
                              <button
                                onClick={() => setLightboxUrl(img.url || `${CATALOGO_URL}/imagenes/${img.filename}`)}
                                className="opacity-0 group-hover:opacity-100 p-1.5 bg-white rounded-full shadow-md mr-1 transition-opacity"
                                title="Ver imagen"
                              >
                                <Eye size={14} className="text-gray-600" />
                              </button>
                              <button
                                onClick={() => handleDeleteImage(img.filename)}
                                className="opacity-0 group-hover:opacity-100 p-1.5 bg-white rounded-full shadow-md ml-1 transition-opacity"
                                title="Eliminar imagen"
                              >
                                <Trash2 size={14} className="text-red-500" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* PDFs / Fichas Section */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <FileText size={16} className="text-green-500" /> Fichas Técnicas (PDF)
                      </h4>
                      <label className="cursor-pointer px-3 py-1.5 rounded-md text-xs font-medium text-white bg-green-600 hover:bg-green-700 transition-colors flex items-center gap-1.5">
                        {uploadingFichas ? (
                          <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Upload size={13} />
                        )}
                        Subir Fichas
                        <input type="file" accept="application/pdf" multiple onChange={handleUploadFichas} className="hidden" disabled={uploadingFichas} />
                      </label>
                    </div>
                    {productFiles.fichas.length === 0 ? (
                      <div className="border-2 border-dashed border-gray-200 rounded-lg p-6 text-center">
                        <FileText size={28} className="mx-auto mb-2 text-gray-300" />
                        <p className="text-xs text-gray-400">No hay fichas cargadas</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {productFiles.fichas.map((ficha) => (
                          <div key={ficha.filename} className="flex items-center justify-between px-3 py-2 rounded-md border border-gray-200 hover:border-green-300 transition-colors group">
                            <a
                              href={ficha.url || `${CATALOGO_URL}/fichas/${ficha.filename}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm text-gray-700 hover:text-green-600 transition-colors flex-1 min-w-0"
                            >
                              <FileText size={16} className="text-green-500 flex-shrink-0" />
                              <span className="truncate">{ficha.filename}</span>
                            </a>
                            <button
                              onClick={() => handleDeleteFicha(ficha.filename)}
                              className="p-1.5 rounded text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0"
                              title="Eliminar ficha"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg sticky bottom-0 z-10">
              <button
                onClick={() => { setShowFilesModal(false); setFilesProduct(null); }}
                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox overlay */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[60] cursor-pointer"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/20 hover:bg-white/40 transition-colors"
          >
            <X size={24} className="text-white" />
          </button>
          <img
            src={lightboxUrl}
            alt="Vista completa"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            onError={(e) => {
              // Mismo fallback que las miniaturas: probar .png -> .jpg -> .jpeg -> .gif
              // si la extension inicial falla. Replica class_producto.php (PHP legacy).
              const currentSrc = e.target.src;
              const baseUrl = currentSrc.replace(/\.[^./]+$/, '');
              const tried = e.target.dataset.tried ? e.target.dataset.tried.split(',') : [];
              const exts = ['.png', '.jpg', '.jpeg', '.gif'].filter(x => !tried.includes(x));
              if (exts.length === 0) return;
              const nextExt = exts[0];
              e.target.dataset.tried = [...tried, nextExt].join(',');
              e.target.src = `${baseUrl}${nextExt}`;
            }}
          />
        </div>
      )}

      {/* Families Management Modal */}
      {showFamiliesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-lg z-10">
              <h3 className="text-lg font-semibold text-gray-900">Gestionar Familias</h3>
              <button onClick={() => setShowFamiliesModal(false)} className="p-1 rounded hover:bg-gray-100 transition-colors">
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            <div className="px-6 py-5">
              {familyError && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-4 flex items-center justify-between">
                  <span className="text-sm">{familyError}</span>
                  <button onClick={() => setFamilyError('')} className="text-red-400 hover:text-red-600"><X size={14} /></button>
                </div>
              )}

              {/* Add new family */}
              <div className="flex items-center gap-2 mb-4">
                <input
                  type="text"
                  value={newFamilyName}
                  onChange={(e) => setNewFamilyName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateFamily()}
                  placeholder="Nueva familia..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={handleCreateFamily}
                  disabled={familySaving || !newFamilyName.trim()}
                  className="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors disabled:opacity-50 flex items-center gap-1.5"
                  style={{ backgroundColor: '#0099FF' }}
                >
                  <Plus size={14} /> Crear
                </button>
              </div>

              {/* Families list */}
              {familiesLoading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#0099FF' }}></div>
                </div>
              ) : familiesWithCount.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">No hay familias registradas</p>
              ) : (
                <div className="space-y-1">
                  {familiesWithCount.map((fam) => (
                    <div key={fam.id} className="flex items-center justify-between px-3 py-2.5 rounded-md border border-gray-200 hover:border-gray-300 transition-colors group">
                      {editingFamily === fam.id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <input
                            type="text"
                            value={editFamilyName}
                            onChange={(e) => setEditFamilyName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleUpdateFamily(fam.id);
                              if (e.key === 'Escape') { setEditingFamily(null); setEditFamilyName(''); }
                            }}
                            className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                          <button
                            onClick={() => handleUpdateFamily(fam.id)}
                            disabled={familySaving}
                            className="p-1 rounded text-green-600 hover:bg-green-50 transition-colors"
                            title="Guardar"
                          >
                            <Save size={14} />
                          </button>
                          <button
                            onClick={() => { setEditingFamily(null); setEditFamilyName(''); }}
                            className="p-1 rounded text-gray-400 hover:bg-gray-100 transition-colors"
                            title="Cancelar"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-gray-900">{fam.nombre}</span>
                            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                              {fam.product_count} producto{fam.product_count !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={() => { setEditingFamily(fam.id); setEditFamilyName(fam.nombre); }}
                              className="p-1.5 rounded text-gray-400 hover:text-blue-600 transition-colors"
                              title="Renombrar"
                            >
                              <Pencil size={13} />
                            </button>
                            <button
                              onClick={() => handleDeleteFamily(fam.id)}
                              disabled={familySaving}
                              className="p-1.5 rounded text-gray-400 hover:text-red-500 transition-colors"
                              title={fam.product_count > 0 ? `No se puede eliminar: ${fam.product_count} productos` : 'Eliminar'}
                            >
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex justify-end px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg sticky bottom-0 z-10">
              <button
                onClick={() => setShowFamiliesModal(false)}
                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Update Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Actualización Masiva</h3>
              <button onClick={() => setShowBulkModal(false)} className="p-1 rounded hover:bg-gray-100 transition-colors">
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            <div className="px-6 py-5">
              <p className="text-sm text-gray-500 mb-4">
                {selectedProducts.size} producto{selectedProducts.size !== 1 ? 's' : ''} seleccionado{selectedProducts.size !== 1 ? 's' : ''}
              </p>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Acción</label>
                <select
                  value={bulkAction}
                  onChange={(e) => { setBulkAction(e.target.value); setBulkValue(''); }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Seleccionar acción...</option>
                  <option value="precio_porcentaje">Ajustar precio de coste (%)</option>
                  <option value="precio_coste_fijo">Establecer precio de coste fijo</option>
                  <option value="cambiar_familia">Cambiar familia</option>
                  <option value="cambiar_visibilidad">Cambiar visibilidad</option>
                  <option value="cambiar_proveedor">Cambiar proveedor</option>
                </select>
              </div>

              {bulkAction === 'precio_porcentaje' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Porcentaje de ajuste</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.1"
                      value={bulkValue}
                      onChange={(e) => setBulkValue(e.target.value)}
                      placeholder="ej: 10 para +10%, -5 para -5%"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <span className="text-sm text-gray-500 font-medium">%</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Positivo para incrementar, negativo para reducir</p>
                </div>
              )}

              {bulkAction === 'precio_coste_fijo' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nuevo precio de coste</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={bulkValue}
                      onChange={(e) => setBulkValue(e.target.value)}
                      placeholder="ej: 12.50"
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <span className="text-sm text-gray-500 font-medium">&euro;</span>
                  </div>
                </div>
              )}

              {bulkAction === 'cambiar_familia' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nueva familia</label>
                  <select
                    value={bulkValue}
                    onChange={(e) => setBulkValue(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Seleccionar familia...</option>
                    {families.map(f => (
                      <option key={f.id} value={f.id}>{f.nombre}</option>
                    ))}
                  </select>
                </div>
              )}

              {bulkAction === 'cambiar_visibilidad' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Visibilidad</label>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setBulkValue('1')}
                      className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium border transition-colors ${
                        bulkValue === '1'
                          ? 'bg-green-50 border-green-400 text-green-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      Visible
                    </button>
                    <button
                      onClick={() => setBulkValue('0')}
                      className={`flex-1 px-4 py-2.5 rounded-md text-sm font-medium border transition-colors ${
                        bulkValue === '0'
                          ? 'bg-gray-200 border-gray-400 text-gray-700'
                          : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      Oculto
                    </button>
                  </div>
                </div>
              )}

              {bulkAction === 'cambiar_proveedor' && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nuevo proveedor</label>
                  <input
                    type="text"
                    value={bulkValue}
                    onChange={(e) => setBulkValue(e.target.value)}
                    placeholder="Nombre del proveedor"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <button
                onClick={() => setShowBulkModal(false)}
                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleBulkUpdate}
                disabled={bulkSaving || !bulkAction || !bulkValue}
                className="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors flex items-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: '#0099FF' }}
              >
                {bulkSaving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Save size={14} />}
                Aplicar a {selectedProducts.size} producto{selectedProducts.size !== 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Bulk Update Modal */}
      {showExcelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 sticky top-0 bg-white rounded-t-lg z-10">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(0,153,255,0.08)' }}>
                  <Upload size={20} style={{ color: '#0099FF' }} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Actualización Masiva desde Excel</h3>
              </div>
              <button onClick={() => setShowExcelModal(false)} className="p-1 rounded hover:bg-gray-100 transition-colors">
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {/* Format info box — Excel unificado, sin selector de tipo */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm font-medium text-blue-800 mb-1">{EXCEL_FORMAT_INFO.title}</p>
                <p className="text-xs text-blue-700 mb-2">{EXCEL_FORMAT_INFO.description}</p>
                <p className="text-xs text-blue-600 font-mono bg-blue-100 rounded px-2 py-1 break-words">
                  {EXCEL_FORMAT_INFO.columns}
                </p>
                <button
                  onClick={handleDownloadTemplate}
                  type="button"
                  className="mt-3 px-3 py-1.5 text-xs font-medium rounded border border-blue-400 text-blue-700 hover:bg-blue-100 transition-colors"
                >
                  📥 Descargar plantilla vacía
                </button>
              </div>

              {/* File input */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                <Upload size={28} className="mx-auto text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 mb-2">Selecciona un archivo Excel o CSV</p>
                <input
                  type="file"
                  accept=".xls,.xlsx,.csv"
                  onChange={(e) => { setExcelFile(e.target.files[0] || null); setExcelResult(null); }}
                  className="block mx-auto text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:text-white file:cursor-pointer"
                  style={{ maxWidth: 320 }}
                />
                <style>{`input[type="file"]::file-selector-button { background-color: #0099FF; }`}</style>
                {excelFile && (
                  <p className="text-xs text-gray-500 mt-2">Archivo: {excelFile.name}</p>
                )}
              </div>

              {/* Result */}
              {excelResult && (
                <div className={`p-4 rounded-lg border ${excelResult.error ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                  <p className={`text-sm font-medium ${excelResult.error ? 'text-red-700' : 'text-green-700'}`}>
                    {excelResult.message}
                  </p>
                  {excelResult.processed !== undefined && (
                    <p className="text-xs text-gray-600 mt-1">
                      {excelResult.processed} filas procesadas, {excelResult.updated} productos actualizados
                    </p>
                  )}
                  {excelResult.errors && excelResult.errors.length > 0 && (
                    <div className="mt-2 max-h-32 overflow-y-auto">
                      <p className="text-xs font-medium text-red-600 mb-1">Errores ({excelResult.errors.length}):</p>
                      <ul className="text-xs text-red-600 space-y-0.5">
                        {excelResult.errors.slice(0, 20).map((e, i) => (
                          <li key={i}>- {e}</li>
                        ))}
                        {excelResult.errors.length > 20 && (
                          <li className="font-medium">... y {excelResult.errors.length - 20} errores más</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg sticky bottom-0 z-10">
              <button
                onClick={() => setShowExcelModal(false)}
                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Cerrar
              </button>
              <button
                onClick={handleExcelUpload}
                disabled={!excelFile || excelUploading}
                className="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#0099FF' }}
              >
                {excelUploading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Procesando...</>
                ) : (
                  <><Upload size={14} /> Subir y Actualizar</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk File Upload Modal */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Image size={20} style={{ color: '#0099FF' }} />
                <h3 className="text-lg font-semibold text-gray-900">Carga Masiva de Archivos</h3>
              </div>
              <button onClick={() => setShowBulkUploadModal(false)} className="p-1 rounded hover:bg-gray-100 transition-colors">
                <X size={18} className="text-gray-400" />
              </button>
            </div>

            <div className="px-6 py-4 overflow-y-auto flex-1 space-y-4">
              <p className="text-sm text-gray-600">
                Seleccione imágenes (PNG, JPG, WEBP) y/o PDFs (fichas técnicas). El nombre del archivo debe ser la <strong>raíz</strong> del producto (ej: <code className="bg-gray-100 px-1 rounded">00123.jpg</code>, <code className="bg-gray-100 px-1 rounded">00123.pdf</code>).
              </p>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                <input
                  type="file"
                  multiple
                  accept=".png,.jpg,.jpeg,.webp,.gif,.pdf"
                  onChange={(e) => { setBulkFiles(e.target.files); setBulkUploadResult(null); }}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {bulkFiles && bulkFiles.length > 0 && (
                  <p className="text-sm text-gray-500 mt-2">{bulkFiles.length} archivo(s) seleccionado(s)</p>
                )}
              </div>

              {/* Results */}
              {bulkUploadResult && !bulkUploadResult.error && (
                <div className="space-y-3">
                  {/* Uploaded successfully */}
                  {bulkUploadResult.uploaded && bulkUploadResult.uploaded.length > 0 && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm font-medium text-green-800 mb-2">
                        Subidos correctamente ({bulkUploadResult.uploaded.length})
                      </p>
                      <ul className="text-xs text-green-700 space-y-1 max-h-32 overflow-y-auto">
                        {bulkUploadResult.uploaded.map((u, i) => (
                          <li key={i} className="flex items-center gap-1">
                            {u.type === 'pdf' ? <FileText size={12} /> : <Image size={12} />}
                            <span className="font-medium">{u.originalFilename}</span> → {u.productName} (#{u.productId})
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Not found */}
                  {bulkUploadResult.not_found && bulkUploadResult.not_found.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                      <p className="text-sm font-medium text-red-800 mb-2">
                        Sin producto ({bulkUploadResult.not_found.length})
                      </p>
                      <ul className="text-xs text-red-700 space-y-0.5 max-h-32 overflow-y-auto">
                        {bulkUploadResult.not_found.map((n, i) => (
                          <li key={i}>
                            <span className="font-medium">{n.filename}</span>
                            {n.raiz && <span> — raíz "{n.raiz}" no encontrada</span>}
                            {n.reason && <span> — {n.reason}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Conflicts */}
                  {bulkUploadResult.conflicts && bulkUploadResult.conflicts.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-sm font-medium text-yellow-800 mb-2">
                        Conflictos — múltiples productos ({bulkUploadResult.conflicts.length})
                      </p>
                      <div className="space-y-3 max-h-48 overflow-y-auto">
                        {bulkUploadResult.conflicts.map((c, idx) => (
                          <div key={idx} className={`bg-white rounded p-2 border ${c.resolved ? 'border-green-300 bg-green-50' : 'border-yellow-300'}`}>
                            <p className="text-xs font-medium text-gray-800 mb-1">
                              {c.resolved ? '✓ ' : ''}{c.filename} — raíz "{c.raiz}" ({c.isPdf ? 'PDF' : 'Imagen'})
                            </p>
                            {c.resolved ? (
                              <p className="text-xs text-green-700">Subido al producto #{c.resolvedProductId}</p>
                            ) : (
                              <div className="flex items-center gap-2">
                                <select
                                  className="text-xs border border-gray-300 rounded px-2 py-1 flex-1"
                                  value={conflictSelections[idx] || ''}
                                  onChange={(e) => setConflictSelections(prev => ({ ...prev, [idx]: e.target.value }))}
                                >
                                  <option value="">Seleccionar producto...</option>
                                  {c.products.map(p => (
                                    <option key={p.id} value={p.id}>#{p.id} — {p.nombre}</option>
                                  ))}
                                </select>
                                <button
                                  onClick={() => handleConflictResolve(idx)}
                                  disabled={!conflictSelections[idx] || conflictUploading[idx]}
                                  className="px-2 py-1 text-xs font-medium rounded text-white disabled:opacity-50 disabled:cursor-not-allowed"
                                  style={{ backgroundColor: '#0099FF' }}
                                >
                                  {conflictUploading[idx] ? '...' : 'Subir'}
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Error */}
              {bulkUploadResult && bulkUploadResult.error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-red-700">{bulkUploadResult.message}</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg sticky bottom-0 z-10">
              <button
                onClick={() => setShowBulkUploadModal(false)}
                className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Cerrar
              </button>
              <button
                onClick={handleBulkFileUpload}
                disabled={!bulkFiles || bulkFiles.length === 0 || bulkUploading}
                className="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ backgroundColor: '#0099FF' }}
              >
                {bulkUploading ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Procesando...</>
                ) : (
                  <><Upload size={14} /> Subir Archivos</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      <CsvImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Importar Productos"
        description="Formato CSV (delimitador ;): raiz;familia;tipo_iva;prec_coste;descrip;auxiliar"
        onImport={handleImportProducts}
      />
    </div>
  );
};

export default ProductManagement;
